import type { VariantProps } from "class-variance-authority"
import { cva } from "class-variance-authority"


/*
  Три правки против того, что приехало из набора:

  1. `transition-all` анимировал в том числе размеры. В шапке грида кнопки
     показывают и прячут подписи — ширина уезжала анимацией, а не менялась.
     Теперь перечислены только цвет, рамка и тень.
  2. `disabled:pointer-events-none` отменял соседний `disabled:cursor-not-allowed`:
     элемент, снятый с проверки попадания, курсор не задаёт вовсе, и правило
     из CLAUDE.md («отключённое — not-allowed») не выполнялось ни разу.
     Клик по `<button disabled>` браузер не пропускает и так. Наведение
     гасится через `not-disabled:hover:` у каждого варианта — иначе
     отключённая кнопка подсвечивалась бы как живая.
  3. Нажатие ничем не отзывалось: было наведение, потом сразу результат.
     `active:` добавлен цветом, без сдвига — на кнопках высотой 24 px
     (плотная шапка) любое смещение читается как дрожание.

  Тень: `shadow-xs` стояла только у `outline`, то есть вторичная кнопка
  выглядела объёмнее первичной. Теперь она у всех заливок, как и у `Input`.
*/
export const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs not-disabled:hover:bg-primary/90 not-disabled:active:bg-primary/80",
        destructive:
          "bg-destructive text-white shadow-xs not-disabled:hover:bg-destructive/90 not-disabled:active:bg-destructive/80 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "btn-quiet-icon border bg-background shadow-xs not-disabled:hover:bg-accent not-disabled:hover:text-accent-foreground not-disabled:active:bg-accent/80 dark:bg-input/30 dark:border-input dark:not-disabled:hover:bg-input/50",
        secondary:
          "btn-quiet-icon bg-secondary text-secondary-foreground shadow-xs not-disabled:hover:bg-secondary/80 not-disabled:active:bg-secondary/70",
        ghost:
          "btn-quiet-icon not-disabled:hover:bg-accent not-disabled:hover:text-accent-foreground not-disabled:active:bg-accent/80 dark:not-disabled:hover:bg-accent/50",
        link: "text-primary underline-offset-4 not-disabled:hover:underline",
      },
      size: {
        "default": "h-9 px-4 py-2 has-[>svg]:px-3",
        "xs": "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        "sm": "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        "lg": "h-10 rounded-md px-6 has-[>svg]:px-4",
        "icon": "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)
export type ButtonVariants = VariantProps<typeof buttonVariants>
